import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import vendorApi from '../services/vendorApi';

const VendorConsignmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consignment, setConsignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConsignmentDetail();
  }, [id]);

  const loadConsignmentDetail = async () => {
    try {
      const data = await vendorApi.getConsignmentDetail(id);
      setConsignment(data);
    } catch (err) {
      setError('Failed to load consignment details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const salePrice = consignment.actual_sale_price || consignment.agreed_price;
  const commission = salePrice * (consignment.commission_rate / 100);
  const payout = consignment.payout_amount || (salePrice - commission);

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/vendor/consignments')}
        sx={{ mb: 2 }}
      >
        Back to Consignments
      </Button>

      <Typography variant="h4" gutterBottom>
        Consignment Detail
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Item Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Item Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {consignment.item?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip label={consignment.status} color="primary" size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {consignment.item?.description || 'No description available'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Consignment Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(consignment.consignment_date).toLocaleDateString()}
                  </Typography>
                </Grid>
                {consignment.sale_date && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Sale Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(consignment.sale_date).toLocaleDateString()}
                    </Typography>
                  </Grid>
                )}
                {consignment.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">{consignment.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Agreed Price
                  </Typography>
                  <Typography variant="h6">
                    ${consignment.agreed_price?.toFixed(2)}
                  </Typography>
                </Box>
                {consignment.actual_sale_price && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Actual Sale Price
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      ${consignment.actual_sale_price.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Commission Rate
                  </Typography>
                  <Typography variant="body1">
                    {consignment.commission_rate}%
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Commission Amount
                  </Typography>
                  <Typography variant="body1" color="warning.main">
                    ${commission.toFixed(2)}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Your Payout
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    ${payout.toFixed(2)}
                  </Typography>
                </Box>
                {consignment.payout_status && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payout Status
                    </Typography>
                    <Chip label={consignment.payout_status} size="small" />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VendorConsignmentDetail;
